/** Bills, supplier credits, and requests/orders/receiving, with filters on top. */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { amount, dec } from '../../lib/money';
import { shortDate } from '../../lib/format';
import { Button, Card, EmptyState, FilterBar, Money, PageHeader, Skeleton, StatusChip, TableWrap, Td, Th, filterClass } from '../../components/ui';
import { KIND_LABEL, STATUS_LABEL, STATUS_TONE, type PurchaseKind, type PurchaseSummary } from './types';

const LISTS = {
  bills: { title: 'Bills', description: 'Supplier invoices, with input VAT and the tax you withhold.', kinds: ['BILL', 'SUPPLIER_CREDIT'] as PurchaseKind[], newKind: 'BILL' as PurchaseKind },
  orders: { title: 'Purchase Orders', description: 'Requests, orders and receiving reports, carried into bills.', kinds: ['PURCHASE_REQUEST', 'PURCHASE_ORDER', 'RECEIVING_REPORT'] as PurchaseKind[], newKind: 'PURCHASE_ORDER' as PurchaseKind },
};

export function PurchaseListPage({ list }: { list: keyof typeof LISTS }): JSX.Element {
  const { can } = useAuth();
  const meta = LISTS[list];
  const [kind, setKind] = useState<PurchaseKind | ''>('');
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [openOnly, setOpenOnly] = useState(false);
  const params = new URLSearchParams({ pageSize: '100' });
  if (status) params.set('status', status);
  if (q.trim()) params.set('q', q.trim());
  if (openOnly) params.set('openOnly', 'true');
  const data = useQuery({
    queryKey: ['purchases', list, kind, params.toString()],
    queryFn: async () => {
      const kinds = kind ? [kind] : meta.kinds;
      const parts = await Promise.all(kinds.map((k) => api<{ data: PurchaseSummary[] }>(`/purchase-documents?${params.toString()}&kind=${k}`)));
      return parts.flatMap((p) => p.data).sort((a, b) => (a.date < b.date ? 1 : -1));
    },
    placeholderData: keepPreviousData,
  });
  return (
    <>
      <PageHeader
        title={meta.title}
        description={meta.description}
        actions={can('bills.manage') && <Link to={`/purchases/new/${meta.newKind.toLowerCase()}`}><Button><Plus aria-hidden="true" className="h-4 w-4" /> New {KIND_LABEL[meta.newKind].toLowerCase()}</Button></Link>}
      />
      <FilterBar>
        <input className={`${filterClass} w-56`} placeholder="Number, supplier invoice or supplier" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search" />
        <select className={filterClass} value={kind} onChange={(e) => setKind(e.target.value as PurchaseKind | '')} aria-label="Document">
          <option value="">All documents</option>
          {meta.kinds.map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
        </select>
        <select className={filterClass} value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status">
          <option value="">All statuses</option>
          {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        {list === 'bills' && <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300"><input type="checkbox" className="h-4 w-4" checked={openOnly} onChange={(e) => setOpenOnly(e.target.checked)} /> Unpaid only</label>}
      </FilterBar>
      <Card>
        {data.isLoading && <Skeleton className="h-48 w-full" />}
        {data.data?.length === 0 && <EmptyState title="Nothing here yet" />}
        {data.data && data.data.length > 0 && (
          <TableWrap>
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-800">
                <Th>Date</Th><Th>Number</Th><Th>Document</Th><Th>Supplier</Th><Th>Their invoice</Th><Th align="right">Total</Th><Th align="right">EWT</Th>{list === 'bills' && <Th align="right">Balance</Th>}<Th>Status</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {data.data.map((d) => (
                <tr key={d.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                  <Td className="whitespace-nowrap">{shortDate(d.date)}</Td>
                  <Td className="whitespace-nowrap font-medium"><Link className="text-brand-700 hover:underline dark:text-brand-100" to={`/purchases/${d.id}`}>{d.number ?? 'Draft'}</Link></Td>
                  <Td className="whitespace-nowrap">{KIND_LABEL[d.kind]}</Td>
                  <Td className="max-w-xs truncate">{d.supplier}</Td>
                  <Td className="whitespace-nowrap">{d.supplierInvoiceNo || '—'}</Td>
                  <Td align="right"><Money>{amount(d.totalAmount)}</Money></Td>
                  <Td align="right"><Money>{amount(d.ewtAmount, true)}</Money></Td>
                  {list === 'bills' && <Td align="right"><Money className={dec(d.balanceDue).gt(0) ? 'font-semibold' : 'text-slate-400'}>{amount(d.balanceDue)}</Money></Td>}
                  <Td><StatusChip tone={STATUS_TONE[d.status]}>{STATUS_LABEL[d.status]}</StatusChip></Td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        )}
      </Card>
    </>
  );
}
